import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import axios from 'axios';
import API_BASE from '../../utils/api';

const API = API_BASE;

const MODULE_LABELS = {
  attendance: 'Attendance', exams: 'Exams', communications: 'Communications', leaves: 'Leaves',
  materials: 'Books & Uniform', timetable: 'Timetable', dailyModules: 'Daily Modules', documents: 'Documents',
  events: 'Events', progressReports: 'Progress Reports', fees: 'Fees', transport: 'Transport',
};
const ACTION_LABELS = { create: 'Create', read: 'View', update: 'Edit', delete: 'Delete' };
const ROLES = [
  { value: 'class_teacher', label: 'Teacher' },
  { value: 'parent', label: 'Parent' },
];

const RolePermissions = () => {
  const [role, setRole] = useState('class_teacher');
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/role-permissions/${role}`, { headers });
      setData(res.data);
      setCurrent(res.data.modules || {});
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  const toggleAction = (moduleName, action) => {
    setCurrent((prev) => {
      const actions = new Set(prev[moduleName] || []);
      if (actions.has(action)) actions.delete(action);
      else actions.add(action);
      return { ...prev, [moduleName]: [...actions] };
    });
  };

  const resetToDefaults = () => setCurrent(data?.defaults || {});

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/role-permissions/${role}`, { modules: current }, { headers });
      showMessage('success', `${ROLES.find((r) => r.value === role).label} permissions saved`);
      fetchPermissions();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">🔑 Role Permissions</h1>
              <p className="text-gray-500 mt-1">Control which modules teachers and parents can access in your school</p>
            </div>
            <ProfileDropdown />
          </div>

          {message.text && (
            <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
              {message.text}
            </div>
          )}

          <div className="flex space-x-1 bg-gray-200 rounded-lg p-1 mb-6 w-fit">
            {ROLES.map((r) => (
              <button key={r.value} onClick={() => setRole(r.value)}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${role === r.value ? 'bg-white text-indigo-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}>
                {r.label}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading...</div>
            ) : !data ? (
              <div className="text-center py-10 text-gray-400">No data</div>
            ) : (
              <>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="p-3 text-sm font-semibold text-gray-600">Module</th>
                        {data.actions.map((a) => (
                          <th key={a} className="p-3 text-sm font-semibold text-gray-600 text-center">{ACTION_LABELS[a] || a}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.allowedModules.map((moduleName) => (
                        <tr key={moduleName} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-sm font-medium text-gray-800">{MODULE_LABELS[moduleName] || moduleName}</td>
                          {data.actions.map((action) => (
                            <td key={action} className="p-3 text-center">
                              <input
                                type="checkbox"
                                checked={(current[moduleName] || []).includes(action)}
                                onChange={() => toggleAction(moduleName, action)}
                                className="w-4 h-4 text-indigo-600 rounded cursor-pointer"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-3">
                  <button onClick={handleSave} disabled={saving}
                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
                    {saving ? 'Saving...' : 'Save Permissions'}
                  </button>
                  <button onClick={resetToDefaults}
                    className="border border-gray-300 text-gray-600 px-6 py-2 rounded-lg hover:bg-gray-50 font-medium">
                    Reset to Defaults
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-4">
                  Unchecking a module here hides it from that role's menu and blocks the underlying pages that enforce it. Some modules are gated only in the menu, not on every individual action.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolePermissions;
